import { useEffect, useRef } from 'react';
import ReactPlayer from 'react-player';
import styled from 'styled-components';

import { RATE_IT_COLORS } from '../constants';

const Fill = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${RATE_IT_COLORS.CARD_BG};
  overflow: hidden;
`;

const Poster = styled.img`
  width: 100%;
  height: 100%;
  object-fit: ${(props) => props.$fit};
  display: block;
`;

const PlayerSlot = styled.div`
  position: absolute;
  inset: 0;

  /* react-player renders a wrapper div around the <video>; both must fill the card, and the
     video itself needs object-fit or it letterboxes inside its own box. */
  video {
    width: 100% !important;
    height: 100% !important;
    object-fit: ${(props) => props.$fit};
  }
`;

/**
 * The moment's media: a video that plays once (reporting `onEnded` so the feed can advance), or a
 * photo.
 *
 * WINDOWED BY `isMounted`, not by the slide. Mobile unmounts the whole page outside the current
 * ±1; on web the slide `<div>` must stay in the DOM or the scroll container's height collapses
 * and scroll-snap breaks. So the slide always renders and only the expensive part — the player,
 * or the full-size image request — is windowed. That is what the ±1 budget was ever about.
 *
 * AUTOPLAY IS MUTED BY DEFAULT, and that is a platform concession rather than a design change.
 * Mobile plays Rate It unmuted on purpose (the crowd noise is part of what is being rated), but
 * browsers block unmuted autoplay without a prior user gesture — and a blocked autoplay leaves a
 * PAUSED video, i.e. a black rectangle, which is worse than a silent one. The feed unmutes as
 * soon as it has a gesture to spend; see `RateItFeed`.
 */
const RateItMedia = ({ item, isActive, isMounted, isMuted, isPaused, onTogglePause, onEnded }) => {
  const isVideo = item.mediaType === 'video';

  /**
   * Crop rather than letterbox when the ratio is unknown.
   *
   * `aspectRatio` is not guaranteed at ingest, and defaulting an unlabelled clip to `contain`
   * would letterbox a portrait video on a full-bleed surface — which reads as broken rather than
   * as deliberate. Cropping is the safer wrong answer.
   */
  const fit = item.aspectRatio == null || item.aspectRatio < 0.9 ? 'cover' : 'contain';

  // Restart a clip when it becomes the active card, so a fan scrolling back sees it from the top
  // rather than wherever it was buffered to.
  const playerRef = useRef(null);
  useEffect(() => {
    if (isActive && playerRef.current?.seekTo) {
      playerRef.current.seekTo(0, 'seconds');
    }
  }, [isActive]);

  if (!isVideo) {
    return (
      <Fill>
        {isMounted ? (
          <Poster src={item.mediaUrl || item.thumbnailUrl} alt="" $fit={fit} />
        ) : (
          <Poster src={item.thumbnailUrl} alt="" $fit={fit} />
        )}
      </Fill>
    );
  }

  return (
    <Fill onClick={onTogglePause}>
      {/* The thumbnail sits underneath the player at all times: it is the poster while the
          player is unmounted, and it covers the first frames while the video buffers. */}
      {item.thumbnailUrl ? <Poster src={item.thumbnailUrl} alt="" $fit={fit} /> : null}

      {isMounted ? (
        <PlayerSlot $fit={fit}>
          <ReactPlayer
            ref={playerRef}
            url={item.mediaUrl}
            width="100%"
            height="100%"
            // Only the active card ever plays; the ±1 neighbours mount to buffer and stay paused.
            playing={isActive && !isPaused}
            muted={isMuted}
            // Plays once: the feed advances to the next moment when the clip ends, the video
            // counterpart to a photo's idle timer. See `RateItFeed`.
            loop={false}
            onEnded={onEnded}
            playsinline
            config={{ file: { attributes: { preload: 'auto' } } }}
          />
        </PlayerSlot>
      ) : null}
    </Fill>
  );
};

export default RateItMedia;
